import { randomUUID } from "node:crypto";

import type { NodeId, WorkflowId } from "./baseTypes";
import type { CredentialRequirement } from "./credentialTypes";
import type { PersistedWorkflowSnapshot } from "./runTypes";
import type { JsonObject, NodeDefinition, PollingTriggerConfig } from "./workflowTypes";
import type { WorkflowDefinition } from "../types";
import { AgentConfigInspector } from "../ai/AgentConfigInspectorFactory";
import { PersistedWorkflowTokenRegistry } from "../workflowSnapshots/PersistedWorkflowTokenRegistry";
import { WorkflowSnapshotCodec } from "../workflowSnapshots/WorkflowSnapshotCodec";

export const DEPLOYMENT_MANIFEST_SCHEMA_VERSION = 2 as const;

export type DeploymentManifestSchemaVersion = typeof DEPLOYMENT_MANIFEST_SCHEMA_VERSION;

export type ManifestTriggerConfig = Readonly<{
  nodeId: NodeId;
  triggerKind: "live" | "test";
  config: JsonObject;
  pollIntervalMs?: number;
  nodeTypeId?: string;
}>;

export type ManifestNodeCredentialShape = Readonly<{
  nodeId: NodeId;
  requirements: ReadonlyArray<CredentialRequirement>;
}>;

export type WorkflowDeploymentManifest<TCanvas = JsonObject> = Readonly<{
  manifestId: string;
  schemaVersion: DeploymentManifestSchemaVersion;
  workflowId: WorkflowId;
  canvas: TCanvas;
  snapshot: PersistedWorkflowSnapshot;
  triggers: ReadonlyArray<ManifestTriggerConfig>;
  credentialShapes: ReadonlyArray<ManifestNodeCredentialShape>;
}>;

export function serializeManifest<TCanvas>(manifest: WorkflowDeploymentManifest<TCanvas>): string {
  return JSON.stringify(manifest);
}

export function deserializeManifest<TCanvas = JsonObject>(raw: string): WorkflowDeploymentManifest<TCanvas> {
  return JSON.parse(raw) as WorkflowDeploymentManifest<TCanvas>;
}

export type ManifestCanvasNode = Readonly<{
  id: NodeId;
  kind: string;
  name: string | undefined;
  type?: string;
  role?: string;
  triggerKind?: "live" | "test";
  declaredOutputPorts?: ReadonlyArray<string>;
  declaredInputPorts?: ReadonlyArray<string>;
}>;

export type ManifestCanvas = Readonly<{
  id: WorkflowId;
  name: string;
  nodes: ReadonlyArray<ManifestCanvasNode>;
  edges: WorkflowDefinition["edges"];
}>;

type TriggerNodeConfigShape = Readonly<{ triggerKind?: "live" | "test" }>;

function isPollingTriggerConfig(config: unknown): config is PollingTriggerConfig {
  return typeof (config as PollingTriggerConfig).getTriggerPollConfig === "function";
}

function resolveNodeTypeName(node: NodeDefinition): string {
  const configToken = node.config?.type as Readonly<{ name?: unknown }> | undefined;
  if (typeof configToken?.name === "string" && configToken.name) {
    return configToken.name;
  }
  const nodeToken = node.type as Readonly<{ name?: unknown }> | undefined;
  if (typeof nodeToken?.name === "string" && nodeToken.name) {
    return nodeToken.name;
  }
  return "Node";
}

function resolveCanvasNode(node: NodeDefinition): ManifestCanvasNode {
  const config = node.config;
  const type = resolveNodeTypeName(node);
  const role = AgentConfigInspector.isAgentNodeConfig(config) ? "agent" : "workflowNode";
  const triggerKind = node.kind === "trigger" ? ((config as TriggerNodeConfigShape).triggerKind ?? "live") : undefined;
  const c = config as {
    declaredOutputPorts?: readonly string[];
    declaredInputPorts?: readonly string[];
  };
  return {
    id: node.id,
    kind: node.kind,
    name: node.name ?? config?.name,
    type,
    role,
    ...(triggerKind !== undefined && { triggerKind }),
    ...(c.declaredOutputPorts !== undefined && { declaredOutputPorts: c.declaredOutputPorts }),
    ...(c.declaredInputPorts !== undefined && { declaredInputPorts: c.declaredInputPorts }),
  };
}

export function emitWorkflowManifest(workflow: WorkflowDefinition): WorkflowDeploymentManifest<ManifestCanvas> {
  const registry = new PersistedWorkflowTokenRegistry();
  registry.registerFromWorkflows([workflow]);
  const codec = new WorkflowSnapshotCodec(registry);
  const snapshot = codec.create(workflow);

  const canvas = {
    id: workflow.id,
    name: workflow.name,
    nodes: workflow.nodes.map(resolveCanvasNode),
    edges: workflow.edges,
  };

  const triggers: ManifestTriggerConfig[] = workflow.nodes
    .filter((n) => n.kind === "trigger")
    .map((n) => {
      const config = n.config;
      const cfg = config as unknown as Record<string, unknown>;
      const triggerKind = (config as TriggerNodeConfigShape).triggerKind ?? "live";
      const pollCfg = isPollingTriggerConfig(config) ? config.getTriggerPollConfig() : undefined;
      const endpointKey = typeof cfg["endpointKey"] === "string" ? cfg["endpointKey"] : undefined;
      const methods = Array.isArray(cfg["methods"]) ? (cfg["methods"] as string[]) : undefined;
      const webhookFields: JsonObject =
        endpointKey !== undefined && methods !== undefined ? { endpointKey, methods } : {};
      const nodeTypeId = registry.getTokenId(config.type) ?? undefined;
      const entry: ManifestTriggerConfig = {
        nodeId: n.id,
        triggerKind,
        config: pollCfg !== undefined ? pollCfg.config : webhookFields,
        nodeTypeId,
      };
      if (pollCfg?.pollIntervalMs !== undefined) {
        return { ...entry, pollIntervalMs: pollCfg.pollIntervalMs };
      }
      return entry;
    });

  const credentialShapes: ManifestNodeCredentialShape[] = workflow.nodes
    .map((n) => {
      const requirements = n.config.getCredentialRequirements?.();
      if (!requirements || requirements.length === 0) return null;
      return { nodeId: n.id, requirements } satisfies ManifestNodeCredentialShape;
    })
    .filter((s): s is ManifestNodeCredentialShape => s !== null);

  return {
    manifestId: randomUUID(),
    schemaVersion: DEPLOYMENT_MANIFEST_SCHEMA_VERSION,
    workflowId: workflow.id,
    canvas,
    snapshot,
    triggers,
    credentialShapes,
  };
}
